import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { kitService, SalvarKitDto } from "services/kitService";

// ─── Query keys ────────────────────────────────────────────────────────────────

export const kitKeys = {
  all: ['kits'] as const,
  list: (termo?: string) => [...kitKeys.all, 'list', { termo }] as const,
  detail: (id: string) => [...kitKeys.all, 'detail', id] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────────

export const useKits = (termo?: string) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: kitKeys.list(termo),
    queryFn: () => kitService.listar(termo),
    retry: getQueryRetryHandler({
      queryKey: kitKeys.list(termo),
      defaultMessage: "Você não possui permissão para consultar kits de produtos.",
    }),
  });
};

export const useKit = (id: string, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: kitKeys.detail(id),
    queryFn: () => kitService.obter(id),
    enabled: !!id && enabled,
    retry: getQueryRetryHandler({
      queryKey: kitKeys.detail(id),
      defaultMessage: "Você não possui permissão para consultar este kit.",
    }),
  });
};

// ─── Mutations ─────────────────────────────────────────────────────────────────

export const useCriarKit = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (data: SalvarKitDto) => kitService.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kitKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para criar kits de produtos."),
  });
};

export const useAlterarKit = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SalvarKitDto }) =>
      kitService.alterar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kitKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: kitKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para alterar kits de produtos."),
  });
};

export const useRemoverKit = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => kitService.remover(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: kitKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: kitKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para remover kits de produtos."),
  });
};
